import * as CL from '@design-system/component-library';
import { useLoaderData, useLocation, useNavigate } from 'react-router';
import { paths } from '../../../../../common/constants/pathVariables';
import { useSearchParams } from '../../../../../common/hooks/useSearchParams.js';
import {
  oldSystemOrdersDisclaimerMsg,
  oldSystemOrdersMsg,
  showOldSystemOrdersMsg,
  t,
} from '../../../../../common/i18n/index.js';
import { isAdminUser } from '../../../../../common/utils/accountUtils.js';
import { CustomerOrderListCoCo } from '../../../../../components/CustomerOrderList/CustomerOrderListCoCo.js';
import { CustomerOrderListWithTable } from '../../../../../components/CustomerOrderList/CustomerOrderListWithTable.js';
import { CustomerOrders, CustomerOrdersSceneTab } from '../../../../../components/CustomerOrders/CustomerOrders.js';
import { Grid } from '../../../../../components/Grid/Grid.js';
import { TableUrlParams } from '../../../../../components/Table/tableConstants.js';
import { useAuth } from '../../../AuthProvider.js';
import { CocoOrderUrlParams, CustomerOrderUrlParams } from './customerOrderConstants';
import type { CocoOrdersResponse } from '../../../../../common/fetch.js';
import type { CustomerOrdersResponse } from '../../../../../generated/api/customerOrdersResponse.js';

const ORDER_SYSTEM_RESET_PARAMS = [
  TableUrlParams.CURSOR,
  TableUrlParams.OFFSET,
  TableUrlParams.PAGE,
  TableUrlParams.SEARCH,
  TableUrlParams.STATUS,
  CustomerOrderUrlParams.CUSTOMER_ORDER_TYPE,
  CocoOrderUrlParams.ORDER_DATE_FROM,
  CocoOrderUrlParams.ORDER_DATE_TO,
  CocoOrderUrlParams.ORDER_TYPE,
  CocoOrderUrlParams.PRODUCT_TYPE,
  CocoOrderUrlParams.ORDER_STATUS,
] as const;

export const getOrdersSystemToggleUrl = (
  location: { pathname: string; search: string; hash: string },
  showOldSystemOrders: boolean
): string => {
  const queryParams = new URLSearchParams(location.search);
  showOldSystemOrders ? queryParams.delete(TableUrlParams.LEGACY) : queryParams.set(TableUrlParams.LEGACY, 'true');
  ORDER_SYSTEM_RESET_PARAMS.forEach(param => queryParams.delete(param));
  return `${location.pathname}?${queryParams}${location.hash}`;
};

export const OrdersPathCoCo = () => {
  const { authenticatedUser } = useAuth();
  const { legacyOrders, cocoOrders } = useLoaderData() as {
    legacyOrders: CustomerOrdersResponse;
    cocoOrders: CocoOrdersResponse;
  };

  const { search, status, productType, customerOrderType, legacySystem } = useSearchParams<{
    search?: string;
    status?: string;
    productType?: string;
    customerOrderType?: string;
    legacySystem?: string;
  }>();

  const isAdmin = isAdminUser(authenticatedUser);
  const { hash, pathname, search: locationSearch } = useLocation();
  const navigate = useNavigate();
  const showOldSystemOrders = legacySystem === 'true';

  const toggleOrderSystem = () => {
    navigate(getOrdersSystemToggleUrl({ pathname, search: locationSearch, hash }, showOldSystemOrders), {
      replace: true,
    });
  };

  return (
    <CustomerOrders tab={CustomerOrdersSceneTab.CUSTOMER_ORDERS}>
      <Grid>
        <CL.Toggle
          labelPosition="right"
          label={t.YSPK(showOldSystemOrdersMsg)}
          value={showOldSystemOrders}
          onChange={toggleOrderSystem}
        />

        {showOldSystemOrders ? (
          <div key="legacy">
            <CL.Disclaimer
              title={t.H4WT(oldSystemOrdersMsg)}
              text={t.U8O2(oldSystemOrdersDisclaimerMsg)}
              className="ds-margin-bottom--2"
              showCloseButton
              icon={<CL.Icon icon="information" type="regular" aria-hidden="true" />}
            />
            <CustomerOrderListWithTable
              basePath={paths.CUSTOMER_ORDER}
              legacySystem={true}
              customerOrders={legacyOrders.searchResults}
              aggregations={legacyOrders.resultCountAggregations}
              loading={false}
              totalItems={legacyOrders.total}
              status={status}
              searchQuery={search}
              authenticatedUser={authenticatedUser}
              productType={productType}
              orderType={customerOrderType}
              showTopic={true}
              isAdmin={isAdmin}
            />
          </div>
        ) : (
          <CustomerOrderListCoCo
            key="coco"
            basePath={paths.CUSTOMER_ORDER}
            orders={cocoOrders.orders}
            loading={false}
            totalItems={cocoOrders.total}
            searchQuery={search}
            authenticatedUser={authenticatedUser}
          />
        )}
      </Grid>
    </CustomerOrders>
  );
};
